import { CheckOutlined, ExclamationCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DatePicker, Input, InputNumber, message, Select, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSales } from "@/modules/sales/services/sales.backend";
import type { Sale, ScheduleItem } from "@/modules/sales/types/sales.types";
import { getAllPaymentSchedules } from "../services/payment-schedules.api";
import { createPayment, getPaymentApiErrorMessage } from "../services/payments.api";
import type { PaymentMethod } from "../types/payments.types";
import {
	buildReceipt,
	flattenPaymentSchedules,
	formatUzs,
	mergeSalesWithPaymentSchedules,
	paymentMethodLabels,
} from "../utils/payments.utils";

function getUnpaidSchedule(sale: Sale): ScheduleItem[] {
	return sale.schedule.filter((item) => item.status !== "paid");
}

type PaymentCreateLocationState = {
	fromDashboard?: boolean;
};

export function PaymentCreatePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();

	const locationState = location.state as PaymentCreateLocationState | null;
	const fromDashboard = locationState?.fromDashboard === true;
	const salesQuery = useQuery({
		queryKey: ["sales", "payments-create"],
		queryFn: () => getSales({ limit: 100, page: 1 }),
		staleTime: 0,
		refetchOnMount: "always",
	});
	const paymentSchedulesQuery = useQuery({
		queryKey: ["payment-schedules", "payments-create"],
		queryFn: () => getAllPaymentSchedules(),
		staleTime: 0,
		refetchOnMount: "always",
	});
	const sales = salesQuery.data?.items ?? [];
	const salesWithSchedules = useMemo(
		() => mergeSalesWithPaymentSchedules(sales, paymentSchedulesQuery.data?.data ?? []),
		[sales, paymentSchedulesQuery.data?.data]
	);

	const payableSales = useMemo(
		() =>
			salesWithSchedules.filter(
				(sale) => sale.status !== "bekor" && getUnpaidSchedule(sale).length > 0
			),
		[salesWithSchedules]
	);
	const requestedSaleId = searchParams.get("saleId");
	const returnClientId = searchParams.get("clientId");
	const firstPayableSale =
		payableSales.find((sale) => sale.id === requestedSaleId) ?? payableSales[0];
	const returnPath = returnClientId
		? `/clients/${returnClientId}`
		: requestedSaleId
			? `/sales/${requestedSaleId}`
			: "/payments";
	const [saleId, setSaleId] = useState(firstPayableSale?.id ?? "");
	const [scheduleId, setScheduleId] = useState<string | null>(
		firstPayableSale ? (getUnpaidSchedule(firstPayableSale)[0]?.id ?? null) : null
	);
	const [amountUzs, setAmountUzs] = useState(0);
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("naqd");
	const [paymentDate, setPaymentDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [receiptNumber, setReceiptNumber] = useState(buildReceipt());
	const [note, setNote] = useState("");

	const selectedSale = payableSales.find((sale) => sale.id === saleId);
	const unpaidRows = selectedSale ? getUnpaidSchedule(selectedSale) : [];
	const selectedSchedule = unpaidRows.find((item) => item.id === scheduleId);
	const scheduleRemaining = selectedSchedule
		? Math.round((selectedSchedule.plannedAmount - selectedSchedule.paidAmount) * 100) / 100
		: 0;
	const schedules = useMemo(
		() => flattenPaymentSchedules(salesWithSchedules),
		[salesWithSchedules]
	);
	const overdueCount = schedules.filter((item) => item.status === "overdue").length;
	const createPaymentMutation = useMutation({
		mutationFn: createPayment,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			if (selectedSale) {
				queryClient.invalidateQueries({ queryKey: ["sale", selectedSale.id] });
			}
		},
	});

	useEffect(() => {
		if (saleId || !firstPayableSale) {
			return;
		}
		setSaleId(firstPayableSale.id);
		setScheduleId(getUnpaidSchedule(firstPayableSale)[0]?.id ?? null);
	}, [firstPayableSale, saleId]);

	useEffect(() => {
		if (!selectedSchedule) {
			return;
		}
		setAmountUzs(scheduleRemaining);
	}, [scheduleRemaining, selectedSchedule]);

	const handleSaleChange = (nextSaleId: string) => {
		const nextSale = payableSales.find((sale) => sale.id === nextSaleId);
		const firstRow = nextSale ? getUnpaidSchedule(nextSale)[0] : undefined;
		setSaleId(nextSaleId);
		setScheduleId(firstRow?.id ?? null);
		setReceiptNumber(buildReceipt());
	};

	const handleSubmit = async () => {
		if (!(selectedSale && selectedSchedule)) {
			message.warning("Grafik qatorini tanlang");
			return;
		}
		if (amountUzs <= 0) {
			message.warning("To'lov summasini kiriting");
			return;
		}
		if (!selectedSchedule.id) {
			message.error("Backend grafik qatori id qaytarmayapti. To'lov yuborib bo'lmaydi.");
			return;
		}

		const paymentPayload = {
			saleId: selectedSale.id,
			amountUzs,
			discountAmountUzs: 0,
			monthsPaid: 1,
			paymentMethod,
			paymentDate,
			receiptNumber,
			note,
			scheduleIds: [selectedSchedule.id],
		};

		try {
			await createPaymentMutation.mutateAsync(paymentPayload);
			message.success("To'lov kiritildi");
			navigate(returnPath, { state: location.state });
		} catch (error: unknown) {
			message.error(getPaymentApiErrorMessage(error, "To'lovni saqlashda xatolik yuz berdi"));
		}
	};

	return (
		<div className="crm-payment-form-page">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={() => {
							if (fromDashboard) {
								navigate(-1);
							} else {
								navigate(returnPath, { state: location.state });
							}
						}}
					>
						{fromDashboard
							? "Bosh sahifaga qaytish"
							: returnClientId
								? "Mijoz kartochkasi"
								: requestedSaleId
									? "Shartnomaga qaytish"
									: "To'lovlar"}
					</Button>
					<h1 style={{ marginTop: "4px" }}>To'lov kiritish</h1>
					<p style={{ color: "var(--crm-text-secondary)" }}>
						{selectedSale ? (
							<span>
								Mijoz:{" "}
								<strong style={{ color: "var(--crm-text-primary)" }}>
									{selectedSale.clientName}
								</strong>{" "}
								· Xona: {selectedSale.roomNumber} ({selectedSale.buildingName})
							</span>
						) : (
							"Bitta grafik qatori"
						)}
					</p>
				</div>
				<Tag color={overdueCount > 0 ? "red" : "green"}>{overdueCount} kechikkan qator</Tag>
			</div>

			<div className="crm-payment-form-grid">
				<div className="crm-card">
					<div className="crm-card-head">
						<h3>To'lov ma'lumotlari</h3>
					</div>
					<div className="crm-card-body">
						{requestedSaleId || returnClientId ? null : (
							<div className="crm-sale-field">
								<span>Sotuv / mijoz</span>
								<Select
									value={saleId}
									onChange={handleSaleChange}
									loading={salesQuery.isLoading || paymentSchedulesQuery.isLoading}
									options={payableSales.map((sale) => ({
										label: `${sale.clientName} - ${sale.buildingName}, ${sale.roomNumber}-xona - #${sale.saleNumber}`,
										value: sale.id,
									}))}
									placeholder="Mijozni tanlang"
									showSearch
									optionFilterProp="label"
								/>
							</div>
						)}

						<div className="crm-sale-field">
							<span>Grafik qatori</span>
							{unpaidRows.length === 0 ? (
								<span className="crm-empty-hint" style={{ color: "var(--crm-text-secondary)" }}>
									{paymentSchedulesQuery.isLoading
										? "Yuklanmoqda..."
										: "Grafik qatorlari topilmadi"}
								</span>
							) : (
								<Select
									value={scheduleId}
									onChange={setScheduleId}
									options={unpaidRows.map((item) => ({
										label: `#${item.index} - ${item.date} - qoldiq ${formatUzs(
											item.plannedAmount - item.paidAmount
										)}`,
										value: item.id ?? "",
									}))}
								/>
							)}
						</div>

						<div className="crm-payment-two-col">
							<div className="crm-sale-field">
								<span>Summa (UZS)</span>
								<InputNumber
									value={amountUzs}
									onChange={(value) => setAmountUzs(value ?? 0)}
									min={0}
									formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
									parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
								/>
							</div>
							<div className="crm-sale-field">
								<span>Usul</span>
								<Select
									value={paymentMethod}
									onChange={setPaymentMethod}
									options={(Object.keys(paymentMethodLabels) as PaymentMethod[]).map((method) => ({
										label: paymentMethodLabels[method],
										value: method,
									}))}
								/>
							</div>
						</div>

						<div className="crm-payment-two-col">
							<div className="crm-sale-field">
								<span>Sana</span>
								<DatePicker
									value={dayjs(paymentDate)}
									onChange={(_, value) => {
										if (!Array.isArray(value) && value) {
											setPaymentDate(value);
										}
									}}
								/>
							</div>
							<div className="crm-sale-field">
								<span>Chek raqami</span>
								<Input
									value={receiptNumber}
									onChange={(event) => setReceiptNumber(event.target.value)}
								/>
							</div>
						</div>

						<div className="crm-sale-field">
							<span>Izoh</span>
							<Input.TextArea
								value={note}
								onChange={(event) => setNote(event.target.value)}
								placeholder="Masalan: iyun oyi to'lovi"
								rows={3}
							/>
						</div>

						<div className="crm-form-actions">
							<Button
								onClick={() => {
									if (fromDashboard) {
										navigate(-1);
									} else {
										navigate(returnPath, { state: location.state });
									}
								}}
							>
								Bekor
							</Button>
							<Button
								className="crm-primary-btn"
								type="primary"
								icon={<CheckOutlined />}
								onClick={handleSubmit}
								loading={createPaymentMutation.isPending}
							>
								Saqlash
							</Button>
						</div>
					</div>
				</div>

				<div className="crm-payment-side">
					<div className="crm-payment-rule amber">
						<ExclamationCircleOutlined />
						<div>
							<strong>Erkin summa</strong>
							<span>
								Summa qator qoldig'idan farq qilishi mumkin. Kamroq summa kiritilsa, qator qisman
								yopiladi; ko'proq kiritilsa, ortiqcha summa keyingi oylarga o'tkaziladi.
							</span>
						</div>
					</div>

					{selectedSchedule ? (
						<div className="crm-card">
							<div className="crm-card-head">
								<h3>Tanlangan qator</h3>
							</div>
							<div className="crm-card-body">
								<div className="crm-kv">
									<span>Reja summa</span>
									<b>{formatUzs(selectedSchedule.plannedAmount)}</b>
								</div>
								<div className="crm-kv">
									<span>To'langan</span>
									<b>{formatUzs(selectedSchedule.paidAmount)}</b>
								</div>
								<div className="crm-kv">
									<span>Qoldiq</span>
									<b className="red">{formatUzs(scheduleRemaining)}</b>
								</div>
							</div>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}

export default PaymentCreatePage;
